import produce from 'immer';
import {
  GET_BULK_PRICE_TARGETS_START,
  GET_BULK_PRICE_TARGETS_SUCCESS,
  GET_BULK_PRICE_TARGETS_FAILED,
  VALIDATE_BULK_PRICE_CSV_START,
  VALIDATE_BULK_PRICE_CSV_SUCCESS,
  VALIDATE_BULK_PRICE_CSV_FAILED,
  START_BULK_PRICE_RUN_START,
  START_BULK_PRICE_RUN_SUCCESS,
  START_BULK_PRICE_RUN_FAILED,
  GET_BULK_PRICE_RUN_START,
  GET_BULK_PRICE_RUN_SUCCESS,
  GET_BULK_PRICE_RUN_FAILED,
  EXPORT_BULK_PRICE_RUN_START,
  EXPORT_BULK_PRICE_RUN_SUCCESS,
  EXPORT_BULK_PRICE_RUN_FAILED,
  RECEIVE_BULK_PRICE_PROGRESS,
  RESET_BULK_PRICE
} from '../actions/bulk_price';


const initialState = {
  targets: [],
  loadingTargets: false,
  validation: null,
  loadingValidation: false,
  runId: null,
  run: null,
  results: [],
  resultsPage: 1,
  resultsPages: 0,
  resultsTotalCount: 0,
  loadingRun: false,
  startingRun: false,
  exportingRun: false,
  progressByTarget: {},
  liveCounters: null
};

const formReducer = produce((state: IBulkPriceState = initialState, action) => {
  switch (action.type) {
    case GET_BULK_PRICE_TARGETS_START: {
      state.loadingTargets = true;
      break;
    }

    case GET_BULK_PRICE_TARGETS_SUCCESS: {
      const { targets } = action.data;

      state.targets = targets;
      state.loadingTargets = false;
      break;
    }

    case GET_BULK_PRICE_TARGETS_FAILED: {
      state.loadingTargets = false;
      break;
    }

    case VALIDATE_BULK_PRICE_CSV_START: {
      state.loadingValidation = true;
      break;
    }

    case VALIDATE_BULK_PRICE_CSV_SUCCESS: {
      state.validation = action.data;
      state.loadingValidation = false;
      break;
    }

    case VALIDATE_BULK_PRICE_CSV_FAILED: {
      state.loadingValidation = false;
      break;
    }

    case START_BULK_PRICE_RUN_START: {
      state.startingRun = true;
      break;
    }

    case START_BULK_PRICE_RUN_SUCCESS: {
      const { runId } = action.data;

      // A new run means a clean slate for progress, results and the live counters.
      state.runId = runId;
      state.run = null;
      state.results = initialState.results;
      state.resultsPage = 1;
      state.resultsPages = 0;
      state.resultsTotalCount = 0;
      state.progressByTarget = {};
      state.liveCounters = null;
      state.startingRun = false;
      break;
    }

    case START_BULK_PRICE_RUN_FAILED: {
      state.startingRun = false;
      break;
    }

    case GET_BULK_PRICE_RUN_START: {
      state.loadingRun = true;
      break;
    }

    case GET_BULK_PRICE_RUN_SUCCESS: {
      const { run, results, page, pages, totalCount } = action.data;

      state.run = run;
      state.runId = run?._id || state.runId;
      state.results = results;
      state.resultsPage = page;
      state.resultsPages = pages;
      state.resultsTotalCount = totalCount;
      state.loadingRun = false;
      break;
    }

    case GET_BULK_PRICE_RUN_FAILED: {
      state.loadingRun = false;
      break;
    }

    case EXPORT_BULK_PRICE_RUN_START: {
      state.exportingRun = true;
      break;
    }

    case EXPORT_BULK_PRICE_RUN_SUCCESS:
    case EXPORT_BULK_PRICE_RUN_FAILED: {
      state.exportingRun = false;
      break;
    }

    // Emits are full snapshots, never deltas, because targets can be processed in parallel and
    // frames can therefore arrive out of order. Keep the highest seq per target and drop the
    // rest, and ignore anything belonging to a run this page is not watching.
    case RECEIVE_BULK_PRICE_PROGRESS: {
      const { data } = action;

      if (!state.runId || String(data?.runId) !== String(state.runId)) {
        break;
      }

      if (data.action === 'BULK_PRICE_FINISHED') {
        if (state.run) {
          state.run.status = data.status;
        }

        break;
      }

      if (data.action !== 'BULK_PRICE_PROGRESS') {
        break;
      }

      const key = String(data.targetIndex);
      const known = state.progressByTarget[key];

      if (!known || data.seq > known.seq) {
        state.progressByTarget[key] = { ...data.target, seq: data.seq };
      }

      if (data.counters) {
        state.liveCounters = data.counters;
      }

      break;
    }

    case RESET_BULK_PRICE: {
      state.targets = initialState.targets;
      state.loadingTargets = initialState.loadingTargets;
      state.validation = initialState.validation;
      state.loadingValidation = initialState.loadingValidation;
      state.runId = initialState.runId;
      state.run = initialState.run;
      state.results = initialState.results;
      state.resultsPage = initialState.resultsPage;
      state.resultsPages = initialState.resultsPages;
      state.resultsTotalCount = initialState.resultsTotalCount;
      state.loadingRun = initialState.loadingRun;
      state.startingRun = initialState.startingRun;
      state.exportingRun = initialState.exportingRun;
      state.progressByTarget = initialState.progressByTarget;
      state.liveCounters = initialState.liveCounters;
      break;
    }
  }
}, initialState);

export default formReducer;
